<template>
  <Dialog :visible="props.visible" class="new-session-dialog" header="false" :closable="false"
    aria-label="New Session Menu" :close-on-escape="true">
    <div v-focustrap aria-modal="true" ref="new-session-dialog-content-ref" class="new-session-dialog-content">
      
      <div class="close-wrapper">
        <button class="close" :onclick="close">
          <img :src="CloseGreyIcon" alt="Close menu" />
        </button>
      </div>

      <h2 class="new-session-dialog-content-title">New Session</h2>
      <div class="new-session-dialog-content-subtitle">
        Select a session type to get started
      </div>

      <Tabs class="new-session-tabs" value="0">
        <TabList>
          <Tab autofocus value="0" class="new-session-tabs-item" size="small">
            With Recording
          </Tab>
          <Tab value="1" class="new-session-tabs-item">
            Without Recording
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel value="0">
            <div class="new-session-dialog-content-panel">
              Before starting a recording: Please kindly seek consent for
              everyone's comfort.
            </div>
            <div class="big-buttons-column">
              <button 
                class="big-button" 
                :aria-label="'Record new audio'" 
                :aria-describedby="'record-audio-desc'"
                @click="router.push({ name: 'Recorder', params: { recorderType: 'audioRecorder' } })"
                @keydown="handleKeyDown($event, 'audio-record')"  
              >
                <div class="big-button-content">
                  <div role="presentation" class="big-button-content-icon">
                    <img aria-label="Microphone icon" :src="MicIcon" alt="AudioRecordIcon" />
                  </div>
                  <div class="big-button-content-content">
                    <div class="big-button-content-content-title">
                      Audio Recording
                    </div>
                    <div  class="big-button-content-content-text">
                      Record a lecture effortlessly and add personal notes.
                    </div>
                  </div>
                  <div id="record-audio-desc" class="sr-only">Audio Recording, record a lecture effortlessly and add personal notes.</div>
                </div>
              </button>

              <button 
                class="big-button" 
                :aria-label="'Screen Recording'" 
                :aria-describedby="'record-screen-desc'"
                @click="router.push({ name: 'ScreenRecorder' })"
                disabled
                @keydown="handleKeyDown($event, 'screen-record')"
              >
                <div class="big-button-content big-button-disabled">
                  <div role="presentation" class="big-button-content-icon">
                    <img aria-hidden="true" :src="ScreenRecordIcon" alt="ScreenRecordIcon" />
                  </div>
                  <div class="big-button-content-content">
                    <div class="big-button-content-content-title">
                      Screen Recording
                    </div>
                    <div class="big-button-content-content-text">
                      Record your screen and add notes.
                    </div>
                  </div>
                  <div id="record-screen-desc" class="sr-only">Screen Recording, record your screen and add personal notes.</div>
                </div>
                <div class="big-button-wrapper-tag" v-if="!isProUser">
                  <Tag value="COMING SOON" class="big-button-tag" />
                </div>
              </button>

            </div>
          </TabPanel>


          <TabPanel value="1">
            <div class="new-session-dialog-content-panel">
              Share thoughtfully: Make sure to get consent and keep
              confidentiality in mind.
            </div>
            <div class="big-buttons-column">
              <button 
                class="big-button" 
                @click="router.push({ name: 'Upload' })"
                @keydown="handleKeyDown($event, 'upload-files')"
                aria-label="Upload files"
                aria-describedby="upload-files-desc"
              >
                <div class="big-button-content">
                  <div role="presentation" class="big-button-content-icon">
                    <img role="presentation" :src="UploadIcon" alt="UploadIcon" />
                  </div>
                  <div class="big-button-content-content">
                    <div class="big-button-content-content-title">
                      Upload Files
                    </div>
                    <div class="big-button-content-content-text">
                      Upload audio, video files or PDFs for editable
                      transcription.
                    </div>
                  </div>
                  <div id="record-audio-desc" class="sr-only">Upload files, upload audio, video files or PDFs for editable transcription. </div>
                </div>
              </button>

              <button 
                class="big-button" 
                :onClick="goToSessionSetup"
                @keydown="handleKeyDown($event, 'new-folder')"
                :disabled="!isProUser"
                aria-label="Live Captions"
                aria-describedby="live-captions-desc"  
              >
                <div class="big-button-content" :class="{ 'big-button-disabled': !isProUser }">
                  <div role="presentation" class="big-button-content-icon">
                    <img role="presentation" :src="LiveCaptionsIcon2" alt="LiveCaptionsIcon2" />
                  </div>
                  <div class="big-button-content-content">
                    <div class="big-button-content-content-title">
                      Live Captions
                      <Tag class="big-button-content-content-title-tag tag-pro" value="PRO" />
                    </div>
                    <div class="big-button-content-content-text">
                      Enable real-time captions for live audio, without recording.
                    </div>
                  </div>
                  <div id="live-captions-desc" class="sr-only">Only available for pro users, Live Captions, enable real-time captions for live audio, without recording.</div>
                </div>
                <div class="big-button-wrapper-tag" v-if="!isProUser">
                  <Tag value="COMING SOON" class="big-button-tag" />
                </div>
              </button>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </Dialog>
</template>

<script setup lang="ts">

import { useRouter } from "vue-router";
import { computed } from "vue";
import { useStore } from "vuex";

const CloseGreyIcon = require("../assets/images/Close-grey.svg") as string;
const LiveCaptionsIcon2 =
  require("../assets/images/LiveCaptionsIcon2.svg") as string;
const MicIcon = require("../assets/images/MicIcon.svg") as string;
const ScreenRecordIcon =
  require("../assets/images/ScreenRecordIcon.svg") as string;
const UploadIcon = require("../assets/images/Upload-white.svg") as string;

const router = useRouter();
const store = useStore();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  setVisible: {
    type: Function,
    required: true,
  },
});

const isProUser = computed(() => {
  if ("subscription" in store.state.user.currentUser.self) {
    if (store.state.user.currentUser.self.subscription) {
      return store.state.user.currentUser.self.subscription.type === "pro";
    } else {
      return false;
    }
  } else {
    return false;
  }
});

function close() {
  props.setVisible(false);
}

function goToSessionSetup() {
  router.push({
    name: "SessionSetup",
  });
}

function handleKeyDown(event: KeyboardEvent, action: string) {
  if (event.key === "Enter") {
    event.preventDefault();
    (event.target as HTMLElement).blur();
    if (action === "audio-record") {
      router.push({ name: "Recorder", params: { recorderType: "audioRecorder" } });
    }
    
    if (action === "screen-record") {
      router.push({ name: "ScreenRecorder" });
    }

    if (action === "upload-files") {
      router.push({ name: "Upload" });
    }

    if (action === "live-captions") {
      goToSessionSetup();
    }
  }
}
</script>

<style lang="scss">
.new-session-dialog {
  width: 45.1rem;

  &-content {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin: 0 2.4rem;
    color: map-get($colours, grey-1);

    &-title {
      @include h3-accent;
      margin-bottom: 0.8rem;
      color: white;
    }

    &-subtitle {
      @include callout;
      margin-bottom: 2.4rem;
    }

    &-panel {
      @include caption;
      margin-bottom: 1.6rem;
    }

    .new-session-tabs-item {
      font-size: 1.2rem;
      letter-spacing: 0.015rem;
    }

  }

  .p-dialog-header {
    display: none;
  }

  .p-dialog-content {
    background-color: map-get($colours, accent-purple-2);
    width: 100%;
    height: 100%;
    border-radius: 1.4rem;
    margin: 0;
    padding: 0;
  }

  .big-buttons-column {
    display: flex;
    flex-direction: column;

    .big-button {
      background: map-get($colours, accent-purple-3);
      height: 7.4rem;
      margin-bottom: 1.6rem;
      border-radius: 14px;
      color: white;
      padding: 1.4rem 1.6rem 1.6rem;

      &:focus-visible {
        box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
        outline: solid 0.2rem rgba(255, 233, 254, 1);
        border: solid 0.1rem map-get($colours, accent-purple3);
      }

      .big-button-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .big-button-content-icon {
          border-radius: 50%;
          background: white;
          min-width: 4rem;
          height: 4rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(25, 25, 25, 0.3);

          img {
            width: 2.4rem;
            height: 2.4rem;
          }
        }

        .big-button-content-content {
          margin-left: 1.6rem;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: space-between;
          font-family: $font-default;

          .big-button-content-content-title {
            font-size: 1.4rem;
            display: flex;
            justify-content: flex-start;
            align-items: center;

            .big-button-content-content-title-tag {
              font-size: 0.8rem;
              margin-left: 0.8rem;
              color: #303030;
            }

            .tag-pro {
              background: #e993ff;
            }

            .tag-basic {
              background: #ffd362;
            }
          }

          .big-button-content-content-text {
            @include caption-small;
            color: #e8e8e8;
          }
        }
      }

      .big-button-disabled {
        filter: blur(0.2rem);
      }

      .big-button-wrapper-tag {
        height: 1%;
        width: 100%;
        position: relative;

        .big-button-tag {
          position: absolute;
          left: 50%;
          font-size: 1rem;
          color: white;
          transform: translate(-50%, -150%);
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }

  .close-wrapper {
    display: flex;
    flex-direction: row-reverse;
    width: 100%;
    margin: 0;

    .close {
      background-color: map-get($colours, accent-purple-2);
      margin-top: 1.6rem;
      width: 2.4rem;
      height: 2.4rem;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        width: 2.4rem;
        height: 2.4rem;
        margin: 0;
      }

      &:focus-visible {
        box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
        outline: solid 0.2rem rgba(255, 233, 254, 1);
        border: solid 0.1rem map-get($colours, accent-purple3);
      }
    }
  }

}
</style>
